import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSkipSummary,
  fetchSkipFilings,
  fetchFiling,
} from "../reducers/skipAnalysisSlice";
import NodeViewer from "./NodeViewer";
import LabReportGrid from "./LabReportGrid";

const TABLE_DAYDATA_COLUMNS = [
  {
    field: "emis_id",
    headerName: "EMIS ID",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "lab_report_id",
    headerName: "Lab Report Id",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "category",
    headerName: "Category",
    flex: 2,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "category_type",
    headerName: "Report Type",
    flex: 2,
    align: "center",
    headerAlign: "center",
    renderCell: (params) =>
      params.value ? "Normal Report" : "Abnormal Report",
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "starttime",
    headerName: "Start Time",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "notes",
    headerName: "Notes",
    flex: 4,
    align: "center",
    headerAlign: "center",
  },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (d) =>
  new Date(Date.now() - d * 86400000).toISOString().slice(0, 10);

const SkipFrame = () => {
  const dispatch = useDispatch();
  const {
    summary,
    filings,
    lab,
    loadingSummary,
    loadingFilings,
    loadingLabReport,
  } = useSelector((s) => s.skipAnalysis);

  const license_key = useSelector((s) => s.authen.loggedInUser?.license_key);

  const [openFilingDialog, setOpenFilingDialog] = useState(false);

  const [startDate, setStartDate] = useState(daysAgoISO(60));
  const [endDate, setEndDate] = useState(todayISO());
  const liverun = false;

  useEffect(() => {
    if (license_key) {
      dispatch(
        fetchSkipSummary({
          start_date: startDate,
          end_date: endDate,
          liverun: true,
          license_key,
        }),
      );
    }
  }, [startDate, endDate, license_key, dispatch]);

  const handleSummaryRowClick = (params) => {
    dispatch(
      fetchSkipFilings({
        start_date: startDate,
        end_date: endDate,
        category_names: params.row.category_names,
        subtest_names: params.row.subtest_names,
        skip_type: params.row.skip_type,
        liverun: true,
        license_key,
      }),
    );
  };

  const handleOpenFiling = (params) => {
    const lab_report_id = params.row?.lab_report_id;

    dispatch(fetchFiling({ lab_report_id, liverun, license_key }));
    setOpenFilingDialog(true);
  };

  const handleCloseFilingDialog = () => {
    setOpenFilingDialog(false);
  };

  return (
    <Box p={2}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Skip Analysis</Typography>

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <TextField
            type="date"
            label="Start Date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextField
            type="date"
            label="End Date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        {loadingSummary ? (
          <CircularProgress />
        ) : (
          <Box sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rowHeight={25}
              rows={summary}
              onRowClick={handleSummaryRowClick}
              getRowClassName={(params) => {
                return params.indexRelativeToCurrentPage % 2 === 0
                  ? "even"
                  : "odd";
              }}
              sx={{
                "& .even": {
                  backgroundColor: "#f9f9f9",
                },
                "& .odd": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundColor: "#e0e0e0 !important",
                },
              }}
              columns={[
                {
                  field: "category_names",
                  headerName: "Category",
                  flex: 2,
                  valueGetter: (params) =>
                    Array.isArray(params) ? params.join(", ") : "—",
                },
                {
                  field: "subtest_names",
                  headerName: "Subtests",
                  flex: 2,
                  valueGetter: (params) =>
                    Array.isArray(params) ? params.join(", ") : "—",
                },
                { field: "skip_type", headerName: "Skip Type", flex: 2 },
                { field: "count", headerName: "Count", flex: 1 },
              ]}
            />
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1">Lab Reports</Typography>
        {loadingFilings ? (
          <CircularProgress />
        ) : (
          <Box sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={filings}
              rowHeight={25}
              onRowClick={handleOpenFiling}
              getRowClassName={(params) => {
                return params.indexRelativeToCurrentPage % 2 === 0
                  ? "even"
                  : "odd";
              }}
              sx={{
                "& .even": {
                  backgroundColor: "#f9f9f9",
                },
                "& .odd": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundColor: "#e0e0e0 !important",
                },
              }}
              columns={TABLE_DAYDATA_COLUMNS}
            />
          </Box>
        )}
      </Paper>

      <Dialog
        open={openFilingDialog}
        onClose={handleCloseFilingDialog}
        maxWidth="xl"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Filing Details
          <Button
            onClick={handleCloseFilingDialog}
            sx={{ float: "right" }}
            color="error"
            variant="outlined"
          >
            Close
          </Button>
        </DialogTitle>

        <DialogContent dividers>
          {loadingLabReport ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading filing...</Typography>
            </Box>
          ) : lab && Object.keys(lab).length > 0 ? (
            <>
              <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    mb: 2,
                    alignItems: "stretch",
                  }}
                >
                  {/* Column 1: Patient Data */}
                  <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                      pr: 2,
                      borderRight: "2px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "primary.main",
                        fontWeight: "bold",
                        mb: 1,
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                        display: "inline-block",
                        pb: 0.5,
                      }}
                    >
                      Patient Data
                    </Typography>

                    <Typography variant="body2">
                      <strong>EMIS ID:</strong> {lab.patient_data?.emis_id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>NHS NUM:</strong> {lab.patient_data?.nhs_num}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Received Date:</strong>{" "}
                      {lab.patient_data?.received_date}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Issue Date:</strong>{" "}
                      {lab.patient_data?.issue_date}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Gender:</strong> {lab.patient_data?.gender}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Age:</strong> {lab.patient_data?.age}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Report ID:</strong>{" "}
                      {lab.patient_data?.lab_report_id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Report Type:</strong>{" "}
                      {lab.patient_data?.report_type}
                    </Typography>
                  </Grid>

                  {/* Column 2: Patient Profile Summary */}
                  <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                      px: 2,
                      borderRight: "2px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "primary.main",
                        fontWeight: "bold",
                        mb: 1,
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                        display: "inline-block",
                        pb: 0.5,
                      }}
                    >
                      Patient Profile Summary
                    </Typography>

                    <Box sx={{ mt: 1 }}>
                      {lab.patient_profile?.health_status &&
                        Object.entries(lab.patient_profile.health_status).map(
                          ([key, val]) => (
                            <Typography key={key} variant="body2">
                              <strong>{key}:</strong> {val?.value || "-"}{" "}
                              {val?.date ? `(${val.date})` : ""}
                            </Typography>
                          ),
                        )}
                    </Box>
                  </Grid>

                  {/* Column 3: Medications */}
                  <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                      px: 2,
                      borderRight: "2px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "primary.main",
                        fontWeight: "bold",
                        mb: 1,
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                        display: "inline-block",
                        pb: 0.5,
                      }}
                    >
                      Medications
                    </Typography>

                    <Box sx={{ mt: 1, maxWidth: 300 }}>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                      >
                        <strong>Acute:</strong>{" "}
                        {lab.patient_profile?.medications?.acute?.length
                          ? lab.patient_profile.medications.acute.join(", ")
                          : "None"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                      >
                        <strong>Repeat:</strong>{" "}
                        {lab.patient_profile?.medications?.repeat?.length
                          ? lab.patient_profile.medications.repeat.join(", ")
                          : "None"}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Column 4: Problems */}
                  <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                      pl: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "primary.main",
                        fontWeight: "bold",
                        mb: 1,
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                        display: "inline-block",
                        pb: 0.5,
                      }}
                    >
                      Problems
                    </Typography>

                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", mt: 1 }}
                      >
                        Active
                      </Typography>
                      {lab.patient_profile?.problems?.active &&
                      Object.keys(lab.patient_profile.problems.active).length >
                        0 ? (
                        Object.entries(lab.patient_profile.problems.active).map(
                          ([problem, date]) => (
                            <Typography key={problem} variant="body2">
                              {problem} {date ? `(${date})` : ""}
                            </Typography>
                          ),
                        )
                      ) : (
                        <Typography variant="body2">None</Typography>
                      )}

                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", mt: 1 }}
                      >
                        Past
                      </Typography>
                      {lab.patient_profile?.problems?.past?.length ? (
                        lab.patient_profile.problems.past.map((p, i) => (
                          <Typography key={i} variant="body2">
                            {p}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2">None</Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <LabReportGrid report={lab.patient_data?.report_data} />

                {Array.isArray(lab.categories) && lab.categories.length > 0 ? (
                  lab.categories.map((cat, idx) => (
                    <Paper
                      key={idx}
                      elevation={1}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderLeft: "5px solid #1976d2",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {cat.category_name}
                      </Typography>

                      {cat.current_ruleset ? (
                        <Box sx={{ pl: 2 }}>
                          <NodeViewer
                            root={cat.current_ruleset}
                            nodeConfigs={{}}
                            onSelectNode={() => {}}
                            selectedNodeId={null}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No ruleset available.
                        </Typography>
                      )}
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No categories found in this report.
                  </Typography>
                )}
              </Paper>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SkipFrame;
